import type { RecipeFamily } from "../../domain/types";
import { option, pantry, slot } from "./helpers";

export const sandwichWrapRecipes: RecipeFamily[] = [
  {
    id: "chickpea-avocado-wrap",
    nameTemplate: "Smashed {protein.name} & {richness.name} wrap",
    translations: {
      tr: { nameTemplate: "Ezilmiş {protein.name} ve {richness.name} dürümü" },
    },
    foodType: "sandwich-wrap",
    primaryCookingLocation: "no-cook",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 12,
    slots: [
      slot("base", "Wrap", true, "tortillas", [
        option("tortillas", 2, "piece"),
      ]),
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 240, "g", "drained", 3),
        option("black-beans", 240, "g", "drained", 2),
      ]),
      slot("richness", "Creamy element", true, "avocado", [
        option("avocado", 1, "piece", "diced", 3),
        option("feta", 100, "g", "crumbled", 1),
      ]),
      slot("vegetable", "Crunchy vegetable", true, "cucumber", [
        option("cucumber", 200, "g", "diced", 3),
        option("bell-pepper", 200, "g", "diced", 2),
      ]),
      slot("green", "Fresh green", true, "spinach", [
        option("spinach", 80, "g"),
      ]),
      slot("dressing", "Dressing", true, "tahini", [
        option("tahini", 2, "tbsp", undefined, 3),
        option("yogurt", 100, "g", undefined, 1),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced"),
      ]),
      slot("herb", "Fresh herb", false, "cilantro", [
        option("cilantro", 15, "g", "chopped", 3),
        option("parsley", 15, "g", "chopped", 2),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "make-filling",
        template:
          "Mash {protein.name}, {richness.name}, {dressing.name}, {acid.name}, {salt.name}, and {black-pepper.name} into a chunky filling.",
        translations: {
          tr: {
            template:
              "{protein.name}, {richness.name}, {dressing.name}, {acid.name}, {salt.name} ve {black-pepper.name} pürüzlü bir iç harç haline getirin.",
          },
        },
      },
      {
        id: "add-vegetable",
        template:
          "Fold {vegetable.name} and {herb.name} into the filling if using the herb.",
        translations: {
          tr: {
            template:
              "{vegetable.name} ve kullanıyorsanız {herb.name} iç harca katlayın.",
          },
        },
      },
      {
        id: "wrap",
        template:
          "Layer {green.name} and the filling over {base.name}. Fold in the sides, roll tightly, and serve.",
        translations: {
          tr: {
            template:
              "{base.name} üzerine {green.name} ve iç harcı paylaştırın. Kenarları içe katlayın, sıkıca sarın ve servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "fat", "acid", "fresh"],
    editorialPriority: 10,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "egg-cucumber-sandwich",
    nameTemplate: "Herby {protein.name} & {vegetable.name} sandwich",
    translations: {
      tr: { nameTemplate: "Otlu {protein.name} ve {vegetable.name} sandviç" },
    },
    foodType: "sandwich-wrap",
    primaryCookingLocation: "no-cook",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 12,
    slots: [
      slot("base", "Bread", true, "bread", [
        option("bread", 4, "piece", "sliced"),
      ]),
      slot("protein", "Protein", true, "egg", [
        option("egg", 4, "piece", "hard-boiled and chopped", 3, {
          safetyCue:
            "Use fully hard-boiled eggs, with both whites and yolks firm, and chop them.",
        }),
        option("tofu", 280, "g", "pressed and crumbled", 2, {
          safetyCue: "Press and crumble the tofu.",
        }),
        option("chickpeas", 240, "g", "drained and mashed", 1, {
          safetyCue: "Drain and mash the chickpeas.",
        }),
      ]),
      slot("vegetable", "Fresh vegetable", true, "cucumber", [
        option("cucumber", 200, "g", "thinly sliced", 3),
        option("tomato", 200, "g", "thinly sliced", 2),
      ]),
      slot("dressing", "Creamy dressing", true, "yogurt", [
        option("yogurt", 100, "g", undefined, 3),
        option("tahini", 2, "tbsp", undefined, 2),
      ]),
      slot("herb", "Fresh herb", true, "parsley", [
        option("parsley", 15, "g", "finely chopped", 3),
        option("cilantro", 15, "g", "finely chopped", 2),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced"),
      ]),
      slot("green", "Fresh green", false, "spinach", [
        option("spinach", 60, "g"),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "prepare-protein",
        template: "{protein.safetyCue}",
        translations: { tr: { template: "{protein.safetyCue}" } },
      },
      {
        id: "mix-filling",
        template:
          "Mix {protein.name}, {dressing.name}, {herb.name}, {acid.name}, {salt.name}, and {black-pepper.name}.",
        translations: {
          tr: {
            template:
              "{protein.name}, {dressing.name}, {herb.name}, {acid.name}, {salt.name} ve {black-pepper.name} karıştırın.",
          },
        },
      },
      {
        id: "assemble",
        template:
          "Top half of {base.name} with {green.name} if using, then add the filling and {vegetable.name}.",
        translations: {
          tr: {
            template:
              "{base.name}'nin yarısının üzerine kullanıyorsanız {green.name}, ardından iç harcı ve {vegetable.name} ekleyin.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Close the sandwiches with the remaining {base.name}, press gently, and cut in half.",
        translations: {
          tr: {
            template:
              "Sandviçleri kalan {base.name} ile kapatın, hafifçe bastırın ve ikiye kesin.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "fat", "acid", "fresh"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "roasted-sweet-potato-black-bean-wrap",
    nameTemplate: "Roasted {root.name} & {protein.name} wrap",
    translations: {
      tr: { nameTemplate: "Fırında {root.name} ve {protein.name} dürümü" },
    },
    foodType: "sandwich-wrap",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 35,
    slots: [
      slot("base", "Wrap", true, "tortillas", [
        option("tortillas", 2, "piece"),
      ]),
      slot("root", "Roasted root", true, "sweet-potato", [
        option("sweet-potato", 400, "g", "cut into 2 cm cubes", 3),
        option("potato", 400, "g", "cut into 2 cm cubes", 1),
      ]),
      slot("protein", "Protein", true, "black-beans", [
        option("black-beans", 240, "g", "drained", 3),
        option("chickpeas", 240, "g", "drained", 2),
      ]),
      slot("vegetable", "Roasting vegetable", true, "bell-pepper", [
        option("bell-pepper", 250, "g", "cut into strips", 3),
        option("zucchini", 250, "g", "cut into thick half-moons", 2),
      ]),
      slot("richness", "Creamy element", true, "avocado", [
        option("avocado", 1, "piece", "sliced", 3),
        option("tahini", 2, "tbsp", undefined, 2),
      ]),
      slot("spice", "Spice", true, "cumin", [
        option("cumin", 1, "tsp", undefined, 3),
        option("smoked-paprika", 1, "tsp", undefined, 2),
      ]),
      slot("oil", "Roasting fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("finish", "Bright finish", false, "lemon", [
        option("lemon", 1, "piece", "juiced", 3),
        option("cilantro", 15, "g", "chopped", 1),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 220°C.",
        translations: { tr: { template: "Fırını 220°C'ye önceden ısıtın." } },
      },
      {
        id: "roast",
        template:
          "Toss {root.name}, {vegetable.name}, and {protein.name} with {oil.name}, {spice.name}, {salt.name}, and {black-pepper.name}. Roast for 22 minutes.",
        translations: {
          tr: {
            template:
              "{root.name}, {vegetable.name} ve {protein.name} ile {oil.name}, {spice.name}, {salt.name} ve {black-pepper.name} karıştırın. 22 dakika fırınlayın.",
          },
        },
      },
      {
        id: "prepare-wrap",
        template:
          "Mash or slice {richness.name} and spread it over {base.name}.",
        translations: {
          tr: {
            template:
              "{richness.name} ezip veya dilimleyip {base.name} üzerine sürün.",
          },
        },
      },
      {
        id: "fill-wrap",
        template:
          "Pile the roasted mixture over {base.name}. Add {finish.name} if using. Fold in the sides and roll tightly.",
        translations: {
          tr: {
            template:
              "Fırınlanmış karışımı {base.name} üzerine koyun. İsterseniz {finish.name} ekleyin. Kenarları içe katlayın ve sıkıca sarın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "fat", "aroma", "sweet"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "roasted-vegetable-feta-wrap",
    nameTemplate: "Roasted {vegetable.name} & {richness.name} wrap",
    translations: {
      tr: {
        nameTemplate: "Fırında {vegetable.name} ve {richness.name} dürümü",
      },
    },
    foodType: "sandwich-wrap",
    primaryCookingLocation: "oven",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 35,
    slots: [
      slot("base", "Wrap", true, "tortillas", [
        option("tortillas", 2, "piece"),
      ]),
      slot("vegetable", "Roasting vegetable", true, "eggplant", [
        option("eggplant", 350, "g", "cut into 2 cm cubes", 3),
        option("zucchini", 350, "g", "cut into thick half-moons", 2),
      ]),
      slot("pepper", "Sweet vegetable", true, "bell-pepper", [
        option("bell-pepper", 250, "g", "cut into strips", 3),
        option("tomato", 250, "g", "halved", 1),
      ]),
      slot("protein", "Protein", true, "chickpeas", [
        option("chickpeas", 200, "g", "drained and dried", 3),
        option("lentils", 220, "g", "cooked and drained", 1),
      ]),
      slot("richness", "Creamy element", true, "feta", [
        option("feta", 100, "g", "crumbled", 3),
        option("avocado", 1, "piece", "sliced", 2),
        option("tahini", 2, "tbsp", undefined, 1),
      ]),
      slot("oil", "Roasting fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      slot("spice", "Spice", true, "smoked-paprika", [
        option("smoked-paprika", 1, "tsp", undefined, 3),
        option("cumin", 1, "tsp", undefined, 2),
      ]),
      slot("finish", "Bright finish", false, "lemon", [
        option("lemon", 1, "piece", "juiced", 3),
        option("balsamic-vinegar", 1, "tbsp", undefined, 1),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "heat-oven",
        template: "Heat the oven to 220°C.",
        translations: { tr: { template: "Fırını 220°C'ye önceden ısıtın." } },
      },
      {
        id: "roast",
        template:
          "Toss {vegetable.name}, {pepper.name}, and {protein.name} with {oil.name}, {spice.name}, {salt.name}, and {black-pepper.name}. Roast for 22 minutes.",
        translations: {
          tr: {
            template:
              "{vegetable.name}, {pepper.name} ve {protein.name} ile {oil.name}, {spice.name}, {salt.name} ve {black-pepper.name} karıştırın. 22 dakika fırınlayın.",
          },
        },
      },
      {
        id: "fill-wraps",
        template:
          "Divide the roasted mixture over {base.name}. Add {richness.name} and {finish.name} if using.",
        translations: {
          tr: {
            template:
              "Fırınlanmış karışımı {base.name} üzerine paylaştırın. {richness.name} ve kullanıyorsanız {finish.name} ekleyin.",
          },
        },
      },
      {
        id: "wrap",
        template: "Fold in the sides, roll tightly, and serve warm.",
        translations: {
          tr: {
            template:
              "Kenarları içe katlayın, sıkıca sarın ve sıcak servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "fat", "acid", "umami"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "smoky-black-bean-quesadilla",
    nameTemplate: "Smoky {protein.name} & {vegetable.name} quesadilla",
    translations: {
      tr: {
        nameTemplate: "Dumanlı {protein.name} ve {vegetable.name} quesadilla",
      },
    },
    foodType: "sandwich-wrap",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 18,
    totalMinutes: 18,
    slots: [
      slot("base", "Tortillas", true, "tortillas", [
        option("tortillas", 4, "piece"),
      ]),
      slot("protein", "Protein", true, "black-beans", [
        option("black-beans", 240, "g", "drained", 3),
        option("chickpeas", 240, "g", "drained and lightly mashed", 2),
      ]),
      slot("vegetable", "Vegetable", true, "bell-pepper", [
        option("bell-pepper", 250, "g", "thinly sliced", 3),
        option("zucchini", 250, "g", "finely diced", 2),
      ]),
      slot("spice", "Spice", true, "smoked-paprika", [
        option("smoked-paprika", 1, "tsp", undefined, 3),
        option("cumin", 1, "tsp", undefined, 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("richness", "Creamy finish", false, "avocado", [
        option("avocado", 1, "piece", "sliced", 3),
        option("tahini", 2, "tbsp", undefined, 1),
      ]),
      slot("finish", "Bright finish", false, "lemon", [
        option("lemon", 1, "piece", "cut into wedges", 3),
        option("lime", 1, "piece", "cut into wedges", 2),
        option("cilantro", 15, "g", "chopped", 1),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "cook-filling",
        template:
          "Warm half the {oil.name} in a frying pan over medium-high heat. Cook {vegetable.name} with {spice.name}, {salt.name}, and {black-pepper.name} for 4 minutes. Add {protein.name} and cook for 2 minutes.",
        translations: {
          tr: {
            template:
              "Bir tavada {oil.name}'nin yarısını orta-yüksek ateşte ısıtın. {vegetable.name} ile {spice.name}, {salt.name} ve {black-pepper.name} 4 dakika pişirin. {protein.name} ekleyip 2 dakika pişirin.",
          },
        },
      },
      {
        id: "fill",
        template:
          "Divide the filling over one half of each {base.name}. Fold to enclose.",
        translations: {
          tr: {
            template:
              "İç harcı her {base.name}'nin bir yarısına paylaştırın. Kapatmak için katlayın.",
          },
        },
      },
      {
        id: "cook-quesadilla",
        template:
          "Wipe the pan, add the remaining {oil.name}, and cook the quesadillas over medium heat for 2 minutes per side until golden and crisp.",
        translations: {
          tr: {
            template:
              "Tavayı silin, kalan {oil.name} ekleyin ve quesadilla'ları orta ateşte her iki taraftan altın rengi ve çıtır olana kadar 2 dakika pişirin.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Cut in half. Serve with {richness.name} and {finish.name} if using.",
        translations: {
          tr: {
            template:
              "İkiye kesin. {richness.name} ve kullanıyorsanız {finish.name} ile servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "fat", "aroma", "heat"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "spiced-tofu-wrap",
    nameTemplate: "Pan-spiced {protein.name} & {veg.name} wrap",
    translations: {
      tr: {
        nameTemplate: "Baharatlı tavada {protein.name} ve {veg.name} dürümü",
      },
    },
    foodType: "sandwich-wrap",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 15,
    slots: [
      slot("base", "Wrap", true, "tortillas", [
        option("tortillas", 2, "piece"),
      ]),
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 300, "g", "pressed and crumbled", 3),
        option("chickpeas", 240, "g", "drained and roughly mashed", 2),
      ]),
      slot("veg", "Vegetable", true, "bell-pepper", [
        option("bell-pepper", 200, "g", "thinly sliced", 3),
        option("spinach", 120, "g", "roughly chopped", 2),
        option("cabbage", 150, "g", "thinly shredded", 1),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 2, "piece", "finely chopped"),
      ]),
      slot("spice", "Spice", true, "cumin", [
        option("cumin", 1, "tsp", undefined, 3),
        option("smoked-paprika", 1, "tsp", undefined, 2),
      ]),
      slot("sauce", "Sauce", true, "soy-sauce", [
        option("soy-sauce", 1, "tbsp", undefined, 3),
        option("lemon", 1, "piece", "juiced", 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("richness", "Creamy finish", false, "avocado", [
        option("avocado", 1, "piece", "sliced", 3),
        option("tahini", 2, "tbsp", undefined, 2),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "cook-filling",
        template:
          "Warm {oil.name} in a frying pan over medium-high heat. Add {garlic.name} and {spice.name}, stir for 30 seconds, then add {protein.name}. Cook for 5 to 6 minutes, stirring occasionally, until lightly golden.",
        translations: {
          tr: {
            template:
              "Bir tavada {oil.name} orta-yüksek ateşte ısıtın. {garlic.name} ve {spice.name} ekleyin, 30 saniye karıştırın, ardından {protein.name} ekleyin. Ara sıra karıştırarak hafifçe kızarıncaya kadar 5-6 dakika pişirin.",
          },
        },
      },
      {
        id: "add-veg",
        template:
          "Add {veg.name} and {sauce.name}. Toss for 2 to 3 minutes until the vegetable is just tender. Season with {salt.name} and {black-pepper.name}.",
        translations: {
          tr: {
            template:
              "{veg.name} ve {sauce.name} ekleyin. Sebze hafifçe pişene kadar 2-3 dakika karıştırın. {salt.name} ve {black-pepper.name} ile tatlandırın.",
          },
        },
      },
      {
        id: "assemble",
        template:
          "Warm each {base.name} in the pan for 30 seconds per side. Spread {richness.name} if using over each wrap, divide the filling between them, fold in the sides, and roll tightly.",
        translations: {
          tr: {
            template:
              "Her {base.name}'yi tavada her iki taraftan 30 saniye ısıtın. Kullanıyorsanız {richness.name} her dürümün üzerine sürün, iç harcı paylaştırın, kenarları içe katlayın ve sıkıca sarın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "umami", "aroma", "fat"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "tofu-tomato-sandwich",
    nameTemplate: "Pressed {protein.name} & {vegetable.name} sandwich",
    translations: {
      tr: { nameTemplate: "Preste {protein.name} ve {vegetable.name} sandviç" },
    },
    foodType: "sandwich-wrap",
    primaryCookingLocation: "no-cook",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 12,
    slots: [
      slot("base", "Bread", true, "bread", [
        option("bread", 4, "piece", "sliced"),
      ]),
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 280, "g", "pressed and thinly sliced", 3),
        option("chickpeas", 200, "g", "drained and roughly mashed", 2),
      ]),
      slot("vegetable", "Fresh vegetable", true, "tomato", [
        option("tomato", 200, "g", "thinly sliced", 3),
        option("cucumber", 200, "g", "thinly sliced", 1),
      ]),
      slot("green", "Leafy green", false, "spinach", [
        option("spinach", 60, "g", undefined, 3),
        option("cabbage", 100, "g", "thinly shredded", 1),
      ]),
      slot("dressing", "Spread", true, "tahini", [
        option("tahini", 2, "tbsp", undefined, 3),
        option("peanut-butter", 2, "tbsp", undefined, 2),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced"),
      ]),
      slot("herb", "Fresh herb", false, "parsley", [
        option("parsley", 15, "g", "roughly chopped", 3),
        option("cilantro", 15, "g", "roughly chopped", 2),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "make-spread",
        template:
          "Whisk {dressing.name}, {acid.name}, {salt.name}, and {black-pepper.name} until smooth.",
        translations: {
          tr: {
            template:
              "{dressing.name}, {acid.name}, {salt.name} ve {black-pepper.name} pürüzsüz olana kadar çırpın.",
          },
        },
      },
      {
        id: "assemble",
        template:
          "Spread the dressing over {base.name}. Layer {protein.name}, {vegetable.name}, and {green.name} if using.",
        translations: {
          tr: {
            template:
              "{base.name} üzerine sosu sürün. {protein.name}, {vegetable.name} ve kullanıyorsanız {green.name} katman katman ekleyin.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Add {herb.name} if using, close the sandwiches, press firmly, and serve.",
        translations: {
          tr: {
            template:
              "İsterseniz {herb.name} ekleyin, sandviçleri kapatın, sıkıca bastırın ve servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "fat", "acid", "fresh"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "tofu-tomato-onion-wrap",
    nameTemplate: "{protein.name}, {vegetable.name} & {onion.name} wrap",
    translations: {
      tr: {
        nameTemplate: "{protein.name}, {vegetable.name} ve {onion.name} dürümü",
      },
    },
    foodType: "sandwich-wrap",
    primaryCookingLocation: "no-cook",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 12,
    slots: [
      slot("base", "Wrap", true, "tortillas", [
        option("tortillas", 2, "piece"),
      ]),
      slot("protein", "Protein", true, "tofu", [
        option("tofu", 280, "g", "pressed and thinly sliced", 3),
        option("chickpeas", 200, "g", "drained and roughly mashed", 2),
      ]),
      slot("vegetable", "Fresh vegetable", true, "tomato", [
        option("tomato", 200, "g", "thinly sliced", 3),
        option("bell-pepper", 200, "g", "thinly sliced", 2),
      ]),
      slot("onion", "Aromatic", true, "onion", [
        option("onion", 1, "piece", "thinly sliced"),
      ]),
      slot("spread", "Spread", true, "tahini", [
        option("tahini", 3, "tbsp", undefined, 3),
        option("peanut-butter", 3, "tbsp", undefined, 2),
      ]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced"),
      ]),
      slot("green", "Leafy green", false, "spinach", [
        option("spinach", 60, "g", undefined, 3),
        option("cabbage", 100, "g", "thinly shredded", 1),
      ]),
      slot("herb", "Fresh herb", false, "parsley", [
        option("parsley", 15, "g", "roughly chopped", 3),
        option("cilantro", 15, "g", "roughly chopped", 2),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "make-spread",
        template:
          "Whisk {spread.name}, {acid.name}, {salt.name}, and {black-pepper.name} until smooth.",
        translations: {
          tr: {
            template:
              "{spread.name}, {acid.name}, {salt.name} ve {black-pepper.name} pürüzsüz olana kadar çırpın.",
          },
        },
      },
      {
        id: "assemble",
        template:
          "Spread the dressing over {base.name}. Layer {protein.name}, {vegetable.name}, {onion.name}, and {green.name} if using.",
        translations: {
          tr: {
            template:
              "{base.name} üzerine sosu sürün. {protein.name}, {vegetable.name}, {onion.name} ve kullanıyorsanız {green.name} katman katman ekleyin.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Scatter {herb.name} if using, roll tightly, and serve immediately.",
        translations: {
          tr: {
            template:
              "İsterseniz {herb.name} serpin, sıkıca sarın ve hemen servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "fat", "acid", "fresh"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "lentil-tahini-pita",
    nameTemplate: "Spiced {protein.name} & {vegetable.name} pita",
    translations: {
      tr: { nameTemplate: "Baharatlı {protein.name} ve {vegetable.name} pita" },
    },
    foodType: "sandwich-wrap",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 14,
    totalMinutes: 14,
    slots: [
      slot("base", "Bread", true, "bread", [
        option("bread", 2, "piece", "halved to form pockets", 3),
        option("tortillas", 2, "piece", undefined, 1),
      ]),
      slot("protein", "Protein", true, "lentils", [
        option("lentils", 250, "g", "cooked", 3),
        option("chickpeas", 240, "g", "drained", 2),
      ]),
      slot("vegetable", "Crunchy vegetable", true, "cucumber", [
        option("cucumber", 200, "g", "thinly sliced", 3),
        option("carrot", 150, "g", "grated", 2),
        option("cabbage", 150, "g", "thinly sliced", 1),
      ]),
      slot("spread", "Spread", true, "tahini", [option("tahini", 3, "tbsp")]),
      slot("acid", "Acid", true, "lemon", [
        option("lemon", 1, "piece", "juiced", 3),
        option("lime", 1, "piece", "juiced", 1),
      ]),
      slot("spice", "Warm spice", true, "cumin", [
        option("cumin", 1, "tsp", undefined, 3),
        option("smoked-paprika", 1, "tsp", undefined, 2),
      ]),
      slot("garlic", "Garlic", true, "garlic", [
        option("garlic", 1, "piece", "finely grated"),
      ]),
      slot("herb", "Fresh herb", false, "parsley", [
        option("parsley", 15, "g", "roughly chopped", 3),
        option("mint", 10, "g", "torn", 2),
        option("cilantro", 15, "g", "roughly chopped", 1),
      ]),
      pantry.water(30),
      pantry.salt,
    ],
    steps: [
      {
        id: "season-lentils",
        template:
          "Toss {protein.name} with {spice.name} and a pinch of {salt.name} in a pan over medium heat. Cook for 3 minutes until warmed and fragrant.",
        translations: {
          tr: {
            template:
              "Orta ateşteki bir tavada {protein.name} ile {spice.name} ve bir tutam {salt.name} karıştırın. Isınana ve kokusu çıkana kadar 3 dakika pişirin.",
          },
        },
      },
      {
        id: "make-tahini",
        template:
          "Whisk {spread.name} with {acid.name} juice, {garlic.name}, {water.name}, and a pinch of {salt.name} until creamy and smooth.",
        translations: {
          tr: {
            template:
              "{spread.name} ile {acid.name} suyu, {garlic.name}, {water.name} ve bir tutam {salt.name} kremsi ve pürüzsüz olana kadar çırpın.",
          },
        },
      },
      {
        id: "fill",
        template:
          "Spread the tahini sauce inside {base.name}. Fill with {protein.name} and {vegetable.name}.",
        translations: {
          tr: {
            template:
              "{base.name} içine tahin sosunu sürün. {protein.name} ve {vegetable.name} ile doldurun.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Scatter {herb.name} if using, roll tightly, and serve immediately.",
        translations: {
          tr: {
            template:
              "İsterseniz {herb.name} serpin, sıkıca sarın ve hemen servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "fat", "acid", "fresh"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "mushroom-avocado-wrap",
    nameTemplate: "Smoky {protein.name} & {avocado.name} wrap",
    translations: {
      tr: { nameTemplate: "Dumanlı {protein.name} ve {avocado.name} dürümü" },
    },
    foodType: "sandwich-wrap",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 12,
    totalMinutes: 12,
    slots: [
      slot("base", "Wrap", true, "tortillas", [
        option("tortillas", 2, "piece"),
      ]),
      slot("protein", "Filling", true, "mushroom", [
        option("mushroom", 350, "g", "thickly sliced", 3),
        option("eggplant", 300, "g", "cut into thin strips", 1),
      ]),
      slot("avocado", "Creamy element", true, "avocado", [
        option("avocado", 1, "piece", "sliced"),
      ]),
      slot("greens", "Fresh greens", true, "spinach", [
        option("spinach", 80, "g", undefined, 3),
        option("cabbage", 100, "g", "thinly sliced", 2),
      ]),
      slot("spice", "Spice", true, "smoked-paprika", [
        option("smoked-paprika", 1.5, "tsp", undefined, 3),
        option("cumin", 1, "tsp", undefined, 2),
      ]),
      slot("acid", "Bright acid", true, "lime", [
        option("lime", 1, "piece", "juiced", 3),
        option("lemon", 1, "piece", "juiced", 1),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      slot("herb", "Fresh herb", false, "cilantro", [
        option("cilantro", 15, "g", "roughly chopped", 3),
        option("scallion", 2, "piece", "sliced", 2),
      ]),
      pantry.salt,
      pantry.pepper,
    ],
    steps: [
      {
        id: "cook-filling",
        template:
          "Heat {oil.name} in a pan over high heat. Cook {protein.name} for 5 to 6 minutes without stirring until golden. Season with {spice.name}, {salt.name}, and {black-pepper.name}.",
        translations: {
          tr: {
            template:
              "Bir tavada {oil.name} yüksek ateşte ısıtın. {protein.name} karıştırmadan altın rengi olana kadar 5-6 dakika pişirin. {spice.name}, {salt.name} ve {black-pepper.name} ile tatlandırın.",
          },
        },
      },
      {
        id: "assemble",
        template:
          "Lay out {base.name}. Arrange {greens.name} in the centre, top with {protein.name} and {avocado.name}. Squeeze over {acid.name} juice.",
        translations: {
          tr: {
            template:
              "{base.name} açın. Ortasına {greens.name} yerleştirin, üzerine {protein.name} ve {avocado.name} koyun. {acid.name} suyunu sıkın.",
          },
        },
      },
      {
        id: "serve",
        template:
          "Scatter {herb.name} if using, fold the sides, roll tightly, and serve.",
        translations: {
          tr: {
            template:
              "İsterseniz {herb.name} serpin, kenarları içe katlayın, sıkıca sarın ve servis yapın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "fat", "acid", "aroma"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "falafel-sumac-flatbread",
    nameTemplate: "Crispy {protein.name} {base.name} wrap",
    translations: {
      tr: { nameTemplate: "Çıtır {protein.name} {base.name} dürümü" },
    },
    foodType: "sandwich-wrap",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 15,
    totalMinutes: 20,
    slots: [
      slot("protein", "Filling", true, "falafel", [
        option("falafel", 200, "g", undefined, 3),
        option("chickpeas", 240, "g", "drained and lightly smashed", 2),
      ]),
      slot("base", "Bread", true, "flatbread", [
        option("flatbread", 2, "piece", undefined, 3),
        option("tortillas", 2, "piece", undefined, 2),
      ]),
      slot("sauce", "Sauce", true, "tahini", [
        option("tahini", 3, "tbsp", undefined, 3),
        option("pomegranate-molasses", 2, "tbsp", undefined, 2),
      ]),
      slot("acid", "Bright finish", true, "lemon", [
        option("lemon", 1, "piece", "juiced", 3),
        option("red-wine-vinegar", 1, "tbsp", undefined, 2),
      ]),
      slot("spice", "Spice garnish", true, "sumac", [
        option("sumac", 1, "tsp", undefined, 3),
        option("smoked-paprika", 0.5, "tsp", undefined, 2),
      ]),
      slot("fresh", "Fresh vegetable", true, "tomato", [
        option("tomato", 200, "g", "roughly chopped", 3),
        option("cucumber", 150, "g", "roughly chopped", 2),
      ]),
      slot("onion", "Sharp garnish", true, "red-onion", [
        option("red-onion", 1, "piece", "very finely sliced", 3),
        option("onion", 1, "piece", "very finely sliced", 2),
      ]),
      slot("herb", "Fresh herb", false, "parsley", [
        option("parsley", 15, "g", "roughly chopped", 3),
        option("dill", 10, "g", "roughly torn", 2),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 2, "tbsp"),
      ]),
      pantry.salt,
    ],
    steps: [
      {
        id: "cook-protein",
        template:
          "If using {protein.name} from a packet, warm in a dry frying pan over medium heat for 4 minutes, turning, until hot and lightly crisped. If using chickpeas, heat {oil.name} in a pan, add chickpeas, and cook 5 minutes until golden; season with {salt.name}.",
        translations: {
          tr: {
            template:
              "Paket {protein.name} kullanıyorsanız, kuru bir tavada orta ateşte çevirerek 4 dakika ısıtın, sıcak ve hafifçe çıtır olana kadar. Nohut kullanıyorsanız, bir tavada {oil.name} ısıtın, nohut ekleyip altın rengi olana kadar 5 dakika pişirin; {salt.name} ile tatlandırın.",
          },
        },
      },
      {
        id: "make-sauce",
        template:
          "Whisk {sauce.name} with {acid.name} and 2 tablespoons of water until smooth and pourable. Season with {salt.name}.",
        translations: {
          tr: {
            template:
              "{sauce.name} ile {acid.name} ve 2 yemek kaşığı suyu pürüzsüz ve akıcı olana kadar çırpın. {salt.name} ile tatlandırın.",
          },
        },
      },
      {
        id: "warm-bread",
        template:
          "Warm {base.name} briefly in a dry pan or directly over a flame for 20 seconds.",
        translations: {
          tr: {
            template:
              "{base.name} kuru bir tavada veya doğrudan ateşin üzerinde 20 saniye ısıtın.",
          },
        },
      },
      {
        id: "assemble",
        template:
          "Spread {sauce.name} down the centre of each {base.name}. Top with {protein.name}, {fresh.name}, {onion.name}, and {herb.name} if using. Sprinkle over {spice.name} and roll tightly to serve.",
        translations: {
          tr: {
            template:
              "Her {base.name}'nin ortasına {sauce.name} sürün. Üzerine {protein.name}, {fresh.name}, {onion.name} ve kullanıyorsanız {herb.name} ekleyin. {spice.name} serpin ve servis için sıkıca sarın.",
          },
        },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "fat", "acid", "aroma", "fresh"],
    editorialPriority: 9,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
  {
    id: "besan-chilla-wrap",
    nameTemplate: "Chickpea flour pancake wrap",
    translations: { tr: { nameTemplate: "Nohut unu gözleme dürümü" } },
    foodType: "sandwich-wrap",
    primaryCookingLocation: "stovetop",
    servings: 2,
    activeMinutes: 25,
    totalMinutes: 30,
    slots: [
      slot("base", "Chickpea flour (batter)", true, "chickpea-flour", [
        option("chickpea-flour", 120, "g"),
      ]),
      slot("sauce", "Hot sauce", true, "sriracha", [
        option("sriracha", 1, "tbsp", undefined, 3),
        option("chili-garlic-sauce", 1, "tbsp", undefined, 2),
        option("tahini", 2, "tbsp", undefined, 1),
      ]),
      slot("protein", "Protein", false, "tofu", [
        option("tofu", 200, "g", "pressed and sliced", 3),
        option("chickpeas", 200, "g", "drained and rinsed", 2),
        option("tempeh", 150, "g", "thinly sliced", 1),
      ]),
      slot("vegetable", "Vegetable", true, "spinach", [
        option("spinach", 60, "g", undefined, 3),
        option("cucumber", 1, "piece", "sliced", 2),
        option("tomato", 1, "piece", "sliced", 1),
      ]),
      slot("onion", "Onion", false, "red-onion", [
        option("red-onion", 0.5, "piece", "thinly sliced", 3),
        option("scallion", 2, "piece", "sliced", 1),
      ]),
      slot("spice", "Spice", false, "cumin", [
        option("cumin", 1, "tsp", undefined, 3),
        option("smoked-paprika", 1, "tsp", undefined, 2),
        option("chili-flakes", 1, "pinch", undefined, 1),
      ]),
      slot("oil", "Cooking fat", true, "olive-oil", [
        option("olive-oil", 1, "tbsp"),
      ]),
      pantry.salt,
      pantry.water(180),
    ],
    steps: [
      {
        id: "make-batter",
        template:
          "Whisk {base.name} with {water.name}, a good pinch of {salt.name}, and {spice.name} if using until smooth. Let rest 5 minutes.",
        translations: {
          tr: {
            template:
              "{base.name} ile {water.name}, bol bir tutam {salt.name} ve kullanıyorsanız {spice.name} pürüzsüz olana kadar çırpın. 5 dakika dinlendirin.",
          },
        },
      },
      {
        id: "cook-pancakes",
        template:
          "Heat a little {oil.name} in a non-stick pan over medium-high heat. Pour in half the batter and swirl to form a thin pancake. Cook 2–3 minutes until the edges lift, then flip and cook 1 minute more. Repeat with remaining batter.",
        translations: {
          tr: {
            template:
              "Yapışmaz bir tavada az miktarda {oil.name} orta-yüksek ateşte ısıtın. Hamurun yarısını dökün ve ince bir gözleme oluşturmak için çevirin. Kenarlar kalkmaya başlayana kadar 2-3 dakika pişirin, ardından çevirin ve 1 dakika daha pişirin. Kalan hamurla tekrarlayın.",
          },
        },
      },
      {
        id: "fill",
        template:
          "Spread {sauce.name} over each pancake. Top with {protein.name} if using, {vegetable.name}, and {onion.name} if using.",
        translations: {
          tr: {
            template:
              "Her gözlemenin üzerine {sauce.name} sürün. Kullanıyorsanız {protein.name}, {vegetable.name} ve kullanıyorsanız {onion.name} ekleyin.",
          },
        },
      },
      {
        id: "roll",
        template: "Roll or fold to serve.",
        translations: { tr: { template: "Servis için sarın veya katlayın." } },
      },
    ],
    targetBalanceRoles: ["starch", "protein", "heat", "fresh", "aroma"],
    editorialPriority: 8,
    sourceNote:
      "Original FlavorCraft recipe family; authored for the production catalog.",
  },
];
